import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getCatalogFiltersRequested } from '../redux/actions/admin-page';
import { CATEGORIES_WITH_NAME_AS_KEY } from './constants/categories-with-name-as-key';
import { IFilterSelectsUI, IFilterSelectsUIData } from './constants/filter-selects';

interface IUseChangeFilters {
  changeFilter: (
    item: IFilterSelectsUIData,
    category: string,
    isSearchItem: boolean
  ) => IFilterSelectsUI[];
  clearFilterCategory: (
    category?: string,
  ) => IFilterSelectsUI[] | [];
  clearAllFilterCategory: () => IFilterSelectsUI[] | [];
}

interface IUseChangeFiltersProps {
  filters?: null | IFilterSelectsUI[]
  isCatalog?: boolean;
}

export const useChangeFilters = ({ filters, isCatalog = false }: IUseChangeFiltersProps): IUseChangeFilters => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search } = useLocation();

  const clearFilterCategory = useCallback((categoryFilter?: string): IFilterSelectsUI[] | [] => {
    const clear = filters?.map((filter) => {
      let newData = filter.data;

      if (filter.category === categoryFilter) {
        setSearchParams((prev) => {
          prev.delete(categoryFilter);
          if (isCatalog) {
            prev.set('page', '1');
          }
          return prev;
        });
        newData = filter.data.map((item) => ({
          ...item,
          isSelected: false,
        }));
      }

      return {
        ...filter,
        data: newData,
      };
    });

    return clear || [];
  }, [setSearchParams, filters, isCatalog]);

  const clearAllFilterCategory = useCallback((): IFilterSelectsUI[] | [] => {
    const clear = filters?.map((filter) => {
      let newData = filter.data;
      setSearchParams((prev) => {
        prev.delete(filter.category);
        if (isCatalog) {
          prev.set('page', '1');
        }
        return prev;
      });
      newData = filter.data.map((item) => ({
        ...item,
        isSelected: false,
      }));

      return {
        ...filter,
        data: newData,
      };
    });

    return clear || [];
  }, [setSearchParams, filters, isCatalog]);

  const changeFilter = useCallback((
    item: IFilterSelectsUIData,
    category: string,
    isSearchItem: boolean,
  ): IFilterSelectsUI[] => {
    let changeFilters: IFilterSelectsUI[] = filters || [];

    if (category && isSearchItem) {
      let currentValues = searchParams.getAll(category)[0]?.split(',') || [];

      const fieldName = category === 'city' ? 'name' : 'id';

      if (!currentValues.includes(item[fieldName])) {
        currentValues.push(item[fieldName]);
      } else {
        currentValues = currentValues.filter((el) => el !== item[fieldName]);
      }

      if (currentValues.length === 0) {
        searchParams.delete(category);
      } else {
        searchParams.set(category, currentValues.join(','));
      }

      setSearchParams(searchParams);

      return changeFilters;
    }

    changeFilters = filters?.map((itemFilter) => {
      let newData = itemFilter.data;

      if (itemFilter.category === category) {
        if (itemFilter.data) {
          newData = itemFilter.data.map((itemData) => {
            if (item.id && itemData.id === item.id) {
              const isAddName = CATEGORIES_WITH_NAME_AS_KEY.includes(category);

              if (!itemData.isSelected) {
                const currentValues = searchParams.getAll(category);
                currentValues.push(isAddName ? item.name : item.id);
                searchParams.set(category, currentValues.join(','));
                if (isCatalog) {
                  searchParams.set('page', '1');
                }

                setSearchParams(searchParams);
              }

              if (itemData.isSelected) {
                const currentValues = searchParams.getAll(category);

                const split = currentValues[0].split(',');
                const newArr = split.filter((i) => (isAddName ? i !== item.name : i !== item.id));

                if (newArr.length === 0) {
                  searchParams.delete(category);
                } else {
                  searchParams.set(category, newArr.join(','));
                }

                setSearchParams(searchParams);
              }

              return {
                ...itemData,
                isSelected: !itemData.isSelected,
              };
            }

            return itemData;
          });
        }
      }
      return { ...itemFilter, data: newData };
    }) || [];

    dispatch(getCatalogFiltersRequested({ search }));

    return changeFilters;
  }, [searchParams, setSearchParams, filters, dispatch, isCatalog, search]);

  return {
    changeFilter,
    clearFilterCategory,
    clearAllFilterCategory,
  };
};
